import logger from '../../lib/logger';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Folder,
  FolderPlus,
  Upload,
  Trash2,
  ChevronRight,
  Home,
  Image,
  Film,
  MoreVertical,
  X,
  Check,
  Download
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { ScrollArea } from '../ui/scroll-area';
import { contentApi } from '../../lib/api';
import { toast } from 'sonner';

// Supported file extensions
const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.tiff', '.tif', '.tga'];
// Video: only MP4 / WebM — .mov / .avi rejected server-side (too fat for fast
// client playback even after transcode).
const VIDEO_EXTENSIONS = ['.mp4', '.webm'];
const ALLOWED_EXTENSIONS = [...IMAGE_EXTENSIONS, ...VIDEO_EXTENSIONS];
const MAX_IMAGE_MB = 200;
const MAX_VIDEO_MB = 300;

const ContentPanel = () => {
  const [content, setContent] = useState({ folders: [], items: [] });
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [folderPath, setFolderPath] = useState([{ id: null, name: 'Content' }]);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedItems, setSelectedItems] = useState(new Set());
  
  // New folder dialog
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  
  const fileInputRef = useRef(null);

  const toggleSelect = (id, e) => {
    e?.stopPropagation();
    setSelectedItems(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const clearSelection = () => setSelectedItems(new Set());

  const handleDeleteSelected = async () => {
    if (selectedItems.size === 0) return;
    if (!window.confirm(`Delete ${selectedItems.size} selected item(s)?`)) return;
    for (const id of selectedItems) {
      try { await contentApi.delete(id); } catch {}
    }
    clearSelection();
    loadContent(currentFolderId);
    toast.success(`${selectedItems.size} item(s) deleted`);
  };

  const loadContent = useCallback(async (folderId = null) => {
    setIsLoading(true);
    try {
      const response = await contentApi.get(folderId);
      setContent({
        folders: response.data.folders || [],
        items: response.data.items || []
      });
      setCurrentFolderId(folderId);
    } catch (error) {
      logger.error('Failed to load content:', error);
      toast.error('Failed to load content');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadContent(null);
  }, [loadContent]);

  // Poll optimization status for any video item still processing. We keep it
  // tight (one poll every 2.5s) so the UI surfaces the finished thumbnail +
  // "ready" state without requiring a manual refresh. Polling stops as soon
  // as no items are still processing.
  useEffect(() => {
    const processing = content.items.filter(
      (it) => it.file_type === 'video' && it.optimization_status === 'processing'
    );
    if (processing.length === 0) return undefined;

    const tick = async () => {
      const updates = await Promise.all(
        processing.map(async (item) => {
          try {
            const res = await contentApi.status(item.id);
            return res.data;
          } catch {
            return null;
          }
        })
      );
      setContent((prev) => ({
        ...prev,
        items: prev.items.map((it) => {
          const u = updates.find((x) => x && x.id === it.id);
          if (!u) return it;
          return { ...it, ...u };
        }),
      }));
    };
    const handle = setInterval(tick, 2500);
    return () => clearInterval(handle);
  }, [content.items]);

  const navigateToFolder = (folderId, folderName) => {
    if (folderId === null) {
      // Navigate to root
      setFolderPath([{ id: null, name: 'Content' }]);
    } else {
      // Add folder to path
      setFolderPath(prev => [...prev, { id: folderId, name: folderName }]);
    }
    loadContent(folderId);
  };

  const navigateToBreadcrumb = (index) => {
    const newPath = folderPath.slice(0, index + 1);
    setFolderPath(newPath);
    loadContent(newPath[newPath.length - 1].id);
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      toast.error('Please enter a folder name');
      return;
    }
    
    try {
      const response = await contentApi.createFolder(newFolderName.trim(), currentFolderId);
      setContent(prev => ({
        ...prev,
        folders: [...prev.folders, response.data]
      }));
      setNewFolderName('');
      setNewFolderOpen(false);
      toast.success(`Folder "${newFolderName}" created`);
    } catch (error) {
      logger.error('Failed to create folder:', error);
      toast.error('Failed to create folder');
    }
  };

  const handleDeleteFolder = async (folderId, folderName) => {
    if (!window.confirm(`Delete folder "${folderName}" and all its contents?`)) {
      return;
    }
    
    try {
      await contentApi.deleteFolder(folderId);
      setContent(prev => ({
        ...prev,
        folders: prev.folders.filter(f => f.id !== folderId)
      }));
      toast.success(`Folder "${folderName}" deleted`);
    } catch (error) {
      logger.error('Failed to delete folder:', error);
      toast.error('Failed to delete folder');
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    for (const file of files) {
      const ext = '.' + file.name.split('.').pop().toLowerCase();
      if (!ALLOWED_EXTENSIONS.includes(ext)) {
        toast.error(`File type ${ext} not supported. Allowed: ${ALLOWED_EXTENSIONS.join(', ')}`);
        continue;
      }

      // File size: videos get 300MB budget, everything else 200MB.
      const isVideo = VIDEO_EXTENSIONS.includes(ext);
      const ceiling = (isVideo ? MAX_VIDEO_MB : MAX_IMAGE_MB) * 1024 * 1024;
      if (file.size > ceiling) {
        toast.error(
          `File "${file.name}" is too large. Maximum size is ${isVideo ? MAX_VIDEO_MB : MAX_IMAGE_MB}MB.`
        );
        continue;
      }

      try {
        setIsUploading(true);
        setUploadProgress(0);
        
        const response = await contentApi.upload(file, currentFolderId, (percent) => {
          setUploadProgress(percent);
        });
        
        setContent(prev => ({
          ...prev,
          items: [...prev.items, response.data]
        }));
        toast.success(`Uploaded "${file.name}"`);
      } catch (error) {
        logger.error('Upload error:', error);
        toast.error(`Failed to upload "${file.name}"`);
      } finally {
        setIsUploading(false);
        setUploadProgress(0);
      }
    }
    
    e.target.value = '';
  };

  const handleDeleteItem = async (itemId, itemName) => {
    if (!window.confirm(`Delete "${itemName}"?`)) {
      return;
    }
    
    try {
      await contentApi.delete(itemId);
      setContent(prev => ({
        ...prev,
        items: prev.items.filter(i => i.id !== itemId)
      }));
      toast.success(`"${itemName}" deleted`);
    } catch (error) {
      logger.error('Failed to delete item:', error);
      toast.error('Failed to delete item');
    }
  };

  /**
   * Download a content item in its "delivered" form:
   *   • Images → original upload (lossless).
   *   • Videos → optimized 720p MP4 when the transcode has completed,
   *     otherwise the original upload (so the file is never missing).
   * Uses a temporary anchor with the download attribute + the file's
   * original filename so OS-level "Save As" dialogs show a friendly name.
   */
  const handleDownloadItem = (item) => {
    const isVideoReady = item.file_type === 'video' && item.optimization_status === 'ready';
    const url = isVideoReady ? contentApi.getOptimizedUrl(item.id) : contentApi.getUrl(item.id);
    const suggestedName = item.original_filename
      || `${item.name || 'content'}${item.file_extension || ''}`;
    const a = document.createElement('a');
    a.href = url;
    a.download = suggestedName;
    // Some browsers ignore `download` for cross-origin URLs; force same-origin
    // by using the relative path (contentApi already returns `/api/...` which
    // is same-origin via the reverse proxy), and open in a new tab as a
    // fallback when the attribute is suppressed.
    a.rel = 'noopener';
    a.target = '_self';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast.success(`Downloading "${suggestedName}"`);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getFileTypeIcon = (item) => {
    if (item.file_type === 'video') {
      return <Film className="w-4 h-4 text-purple-400" />;
    }
    return <Image className="w-4 h-4 text-blue-400" />;
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header with breadcrumb */}
      <div className="p-3 border-b border-zinc-700">
        {/* Breadcrumb */}
        <div className="flex items-center gap-1 text-xs text-zinc-400 mb-3 overflow-x-auto">
          {folderPath.map((folder, index) => (
            <React.Fragment key={folder.id || 'root'}>
              {index > 0 && <ChevronRight className="w-3 h-3 flex-shrink-0" />}
              <button
                onClick={() => navigateToBreadcrumb(index)}
                className={`hover:text-white flex-shrink-0 ${
                  index === folderPath.length - 1 ? 'text-white font-medium' : ''
                }`}
              >
                {index === 0 ? <Home className="w-3 h-3" /> : folder.name}
              </button>
            </React.Fragment>
          ))}
        </div>
        
        {/* Actions */}
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            className="flex-1 h-8 text-xs border-zinc-700 hover:bg-zinc-800"
            onClick={() => setNewFolderOpen(true)}
          >
            <FolderPlus className="w-3 h-3 mr-1" />
            New Folder
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="flex-1 h-8 text-xs border-zinc-700 hover:bg-zinc-800"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
          >
            <Upload className="w-3 h-3 mr-1" />
            {isUploading ? `${uploadProgress}%` : 'Upload'}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept={ALLOWED_EXTENSIONS.join(',')}
            multiple
            onChange={handleFileUpload}
            className="hidden"
          />
        </div>
        
        {/* Upload progress */}
        {isUploading && (
          <div className="mt-2 h-1 bg-zinc-700 rounded-full overflow-hidden">
            <div 
              className="h-full bg-blue-500 transition-all duration-300"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
        )}
      </div>

      {/* Content grid */}
      <ScrollArea className="flex-1">
        <div className="p-3">
          {isLoading ? (
            <div className="text-center text-zinc-500 py-8">Loading...</div>
          ) : content.folders.length === 0 && content.items.length === 0 ? (
            <div className="text-center text-zinc-500 py-8">
              <p>No content yet</p>
              <p className="text-xs mt-1">Upload images or videos to get started</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Folders */}
              {content.folders.length > 0 && (
                <div>
                  <h4 className="text-xs font-medium text-zinc-400 mb-2">Folders</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {content.folders.map(folder => (
                      <div
                        key={folder.id}
                        className="group relative bg-zinc-800/50 rounded-lg p-3 hover:bg-zinc-700/50 cursor-pointer border border-zinc-700/50"
                        onClick={() => navigateToFolder(folder.id, folder.name)}
                      >
                        <div className="flex items-center gap-2">
                          <Folder className="w-5 h-5 text-yellow-500" />
                          <span className="text-sm text-white truncate">{folder.name}</span>
                        </div>
                        
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              className="absolute top-2 right-2 p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-zinc-600"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <MoreVertical className="w-3 h-3" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent className="bg-zinc-800 border-zinc-700">
                            <DropdownMenuItem
                              className="text-red-400 focus:text-red-400"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteFolder(folder.id, folder.name);
                              }}
                            >
                              <Trash2 className="w-3 h-3 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Files */}
              {content.items.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-xs font-medium text-zinc-400">Files ({content.items.length})</h4>
                    {selectedItems.size > 0 && (
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] text-teal-400">{selectedItems.size} selected</span>
                        <Button size="sm" variant="ghost" className="h-6 text-[10px] text-zinc-400 px-1" onClick={clearSelection}>
                          <X className="w-3 h-3" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-6 text-[10px] text-red-400 px-1" onClick={handleDeleteSelected}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-1.5">
                    {content.items.map(item => {
                      const isSelected = selectedItems.has(item.id);
                      return (
                        <div
                          key={item.id}
                          className={`group relative bg-zinc-800/50 rounded-md overflow-hidden border cursor-pointer transition-colors ${
                            isSelected ? 'border-teal-500 ring-1 ring-teal-500/30' : 'border-zinc-700/50 hover:border-zinc-600'
                          }`}
                          onClick={(e) => toggleSelect(item.id, e)}
                        >
                          {/* Selection indicator */}
                          <div className={`absolute top-1 left-1 z-10 w-4 h-4 rounded-sm border flex items-center justify-center transition-colors ${
                            isSelected ? 'bg-teal-500 border-teal-500' : 'border-zinc-500/50 bg-black/30 opacity-0 group-hover:opacity-100'
                          }`}>
                            {isSelected && <Check className="w-3 h-3 text-white" />}
                          </div>

                          {/* Small thumbnail */}
                          <div className="aspect-square bg-zinc-900 flex items-center justify-center relative">
                            {item.file_type === 'image' ? (
                              <img
                                src={contentApi.getUrl(item.id)}
                                alt={item.name}
                                className="w-full h-full object-cover"
                                loading="lazy"
                              />
                            ) : item.thumbnail_path ? (
                              <img
                                src={contentApi.getThumbnailUrl(item.id)}
                                alt={item.name}
                                className="w-full h-full object-cover"
                                loading="lazy"
                              />
                            ) : (
                              <Film className="w-5 h-5 text-zinc-600" />
                            )}
                            {item.file_type === 'video' && item.optimization_status === 'processing' && (
                              <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
                                <span className="text-[9px] text-fuchsia-300 font-medium animate-pulse">
                                  Optimizing…
                                </span>
                              </div>
                            )}
                            {item.file_type === 'video' && item.optimization_status === 'ready' && (
                              <div className="absolute top-1 right-1 bg-fuchsia-600 rounded px-1 text-[8px] text-white font-medium">
                                720p
                              </div>
                            )}
                          </div>
                          
                          {/* Compact info */}
                          <div className="p-1.5">
                            <span className="text-[10px] text-white truncate block leading-tight">{item.name}</span>
                            <span className="text-[9px] text-zinc-500">
                              {formatFileSize(item.file_size)}
                              {item.width && item.height && ` ${item.width}x${item.height}`}
                            </span>
                          </div>
                          
                          {/* Actions */}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button
                                className="absolute top-1 right-1 p-0.5 rounded bg-black/50 opacity-0 group-hover:opacity-100 hover:bg-black/70"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <MoreVertical className="w-3 h-3" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent className="bg-zinc-800 border-zinc-700">
                              <DropdownMenuItem
                                onClick={() => handleDownloadItem(item)}
                                data-testid={`download-content-${item.id}`}
                              >
                                <Download className="w-3 h-3 mr-2" />
                                Download
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="text-red-400 focus:text-red-400"
                                onClick={() => handleDeleteItem(item.id, item.name)}
                              >
                                <Trash2 className="w-3 h-3 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </ScrollArea>

      {/* New Folder Dialog */}
      <Dialog open={newFolderOpen} onOpenChange={setNewFolderOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-700">
          <DialogHeader>
            <DialogTitle>Create New Folder</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Input
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder="Folder name"
              className="bg-zinc-800 border-zinc-700"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreateFolder();
              }}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewFolderOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateFolder}>
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContentPanel;
